import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import MonthNav from '@/components/feature/MonthNav';
import { useStore } from '@/hooks/useStore';

function fmt(v: number) { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }

const PAGAMENTO_ICONS: Record<string, string> = {
  dinheiro: 'ri-money-dollar-circle-line',
  pix: 'ri-qr-code-line',
  cartao: 'ri-bank-card-line',
};
const PAGAMENTO_COLORS: Record<string, string> = {
  dinheiro: 'text-green-400',
  pix: 'text-yellow-400',
  cartao: 'text-orange-400',
};

export default function DashboardPage() {
  const {
    produtos, vendas, despesas, aportes, retiradas,
    lucroOperacional, capitalNaoRecuperado,
    retiradaSegura, totalDescontos,
  } = useStore();

  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());

  const mesPad = String(mes).padStart(2, '0');
  const anoStr = String(ano);

  // Filtro mensal
  const vendasMes = vendas.filter(v => v.data?.startsWith(`${anoStr}-${mesPad}`));
  const despesasMes = despesas.filter(d => d.data?.startsWith(`${anoStr}-${mesPad}`));
  const aportesMes = aportes.filter(a => a.data?.startsWith(`${anoStr}-${mesPad}`));
  const retiradasMes = retiradas.filter(r => r.data?.startsWith(`${anoStr}-${mesPad}`));

  const totalBrutoMes = vendasMes.reduce((acc, v) => acc + v.valorTotal, 0);
  const totalVendidoMes = vendasMes.reduce((acc, v) => acc + v.valorLiquido, 0);
  const custoVendasMes = vendasMes.reduce((acc, v) => {
    const produto = produtos.find(p => p.id === v.produtoId);
    const custoUnitario = produto ? produto.custo + produto.custoExtra : 0;
    return acc + (custoUnitario * v.quantidade);
  }, 0);
  const totalDespesasMes = despesasMes.reduce((acc, d) => acc + d.valor, 0);
  const totalAportesMes = aportesMes.reduce((acc, a) => acc + a.valor, 0);
  const totalRetiradasMes = retiradasMes.reduce((acc, r) => acc + r.valor, 0);
  const lucroMes = totalBrutoMes - custoVendasMes - totalDespesasMes;
  const taxasMes = vendasMes.reduce((acc, v) => acc + (v.valorTotal - v.valorLiquido - (v.valorDesconto ?? 0)), 0);
  const descontosMes = vendasMes.reduce((acc, v) => acc + (v.valorDesconto ?? 0), 0);
  const retiradaSeguraMes = Math.max(0, lucroMes - totalRetiradasMes);


  return (
    <AppLayout>
      <TopBar title="Dashboard" subtitle="Visão geral da PiotFit" />
      <div className="pt-14 md:pt-16 p-3 md:p-6 space-y-3 md:space-y-4 animate-fade-in-up">

        {/* MonthNav */}
        <MonthNav mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a); }} />



        {/* Linha 1: Vendas Brutas — largo em cima */}
        <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
          <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Vendas Brutas</p>
          <p className="text-xl md:text-3xl font-bold font-mono text-neon">{fmt(totalBrutoMes)}</p>
          <p className="text-[10px] md:text-xs text-dark-muted mt-1">Líquido: {fmt(totalVendidoMes)} · Taxas: {fmt(taxasMes)} · Descontos: {fmt(descontosMes)}</p>
        </div>

        {/* Linha 2: Custo + Despesas + Descontos + Taxas + Aportes + Retiradas */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Custo das Vendas</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-red-400">{fmt(custoVendasMes)}</p>
            <p className="text-[10px] md:text-xs text-dark-muted mt-1">{vendasMes.reduce((acc, v) => acc + v.quantidade, 0)} unidades</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Despesas</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-red-400">{fmt(totalDespesasMes)}</p>
            <p className="text-[10px] md:text-xs text-dark-muted mt-1">{despesasMes.length} despesa(s)</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Descontos</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-orange-400">{fmt(descontosMes)}</p>
            <p className="text-[10px] md:text-xs text-dark-muted mt-1">concedidos</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Taxas Maquininha</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-orange-400">{fmt(taxasMes)}</p>
            <p className="text-[10px] md:text-xs text-dark-muted mt-1">cartão · débito · crédito</p>
          </div>

          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Aportes no Mês</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-yellow-400">{fmt(totalAportesMes)}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Retiradas no Mês</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-orange-400">{fmt(totalRetiradasMes)}</p>
          </div>
        </div>

        {/* Lucro do Mês — destaque grande */}
        <div className={`rounded-xl p-4 md:p-8 border flex flex-col items-center justify-center text-center ${lucroMes >= 0 ? 'bg-neon/5 border-neon/30' : 'bg-red-500/5 border-red-500/30'}`}>
          <div className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl mb-3 ${lucroMes >= 0 ? 'bg-neon/15' : 'bg-red-500/15'}`}>
            <i className={`ri-line-chart-line text-xl md:text-2xl ${lucroMes >= 0 ? 'text-neon' : 'text-red-400'}`}></i>
          </div>
          <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Lucro Líquido do Mês</p>
          <p className={`text-2xl md:text-4xl font-bold font-mono ${lucroMes >= 0 ? 'text-neon' : 'text-red-400'}`}>{fmt(lucroMes)}</p>
          <p className="text-[10px] md:text-xs text-dark-muted mt-1 md:mt-2">{fmt(totalBrutoMes)} − {fmt(custoVendasMes)} − {fmt(totalDespesasMes)}</p>
        </div>

        {/* Linha 3: Cap. Não Recuperado + Retirada Segura (geral) */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Cap. Não Recuperado</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-yellow-400">{fmt(capitalNaoRecuperado)}</p>
            <p className="text-[10px] md:text-xs text-dark-muted mt-1">Acumulado geral</p>
          </div>
          <div className={`rounded-xl p-3 md:p-6 border flex flex-col items-center justify-center text-center ${retiradaSeguraMes > 0 ? 'bg-neon/5 border-neon/30' : 'bg-dark-card border-dark-border'}`}>
            <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1 md:mb-2">Retirada Segura</p>
            <p className={`text-lg md:text-2xl font-bold font-mono ${retiradaSeguraMes > 0 ? 'text-neon' : 'text-dark-muted'}`}>{fmt(retiradaSeguraMes)}</p>
            <p className="text-[10px] md:text-xs text-dark-muted mt-1">Lucro do mês − Retiradas do mês</p>
          </div>
        </div>

        {/* Linha 4: contadores */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-5 flex flex-col items-center justify-center text-center gap-2">
            <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-dark-card2">
              <i className="ri-shirt-line text-lg md:text-xl text-neon"></i>
            </div>
            <div>
              <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider">Produtos</p>
              <p className="text-xl md:text-2xl font-bold font-mono text-dark-text">{produtos.length}</p>
              <p className="text-[10px] md:text-xs text-dark-muted">{produtos.filter(p => p.status === 'ativo').length} ativos</p>
            </div>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-5 flex flex-col items-center justify-center text-center gap-2">
            <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-dark-card2">
              <i className="ri-receipt-line text-lg md:text-xl text-neon"></i>
            </div>
            <div>
              <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider">Vendas no Mês</p>
              <p className="text-xl md:text-2xl font-bold font-mono text-dark-text">{vendasMes.length}</p>
              <p className="text-[10px] md:text-xs text-dark-muted">transações</p>
            </div>
          </div>
        </div>

        {/* Últimas vendas do mês */}
        <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-dark-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-history-line text-neon text-sm"></i>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-dark-text uppercase tracking-wider">Vendas do Mês</h3>
            </div>
            <span className="text-[10px] md:text-xs text-dark-muted bg-dark-card2 px-2 py-0.5 rounded-full">{vendasMes.length} registros</span>
          </div>
          {vendasMes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-dark-muted">
              <div className="w-10 h-10 flex items-center justify-center mb-2">
                <i className="ri-shopping-bag-3-line text-2xl"></i>
              </div>
              <p className="text-xs">Nenhuma venda neste mês</p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {vendasMes.slice(0, 5).map(v => (
                <div key={v.id} className="bg-dark-card2 rounded-xl p-3">
                  {/* Cabeçalho */}
                  <div className="mb-2">
                    <p className="font-semibold text-dark-text text-sm truncate">{v.produtoNome}</p>
                    <p className="text-xs text-dark-muted mt-0.5">{new Date(v.data).toLocaleDateString('pt-BR')}</p>
                  </div>
                  {/* Grid de detalhes */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-dark-card rounded-lg p-2 text-center">
                      <p className="text-[9px] text-dark-muted uppercase mb-0.5">Qtd</p>
                      <p className="text-xs font-bold font-mono text-dark-text">{v.quantidade}x</p>
                    </div>
                    <div className="bg-dark-card rounded-lg p-2 text-center">
                      <p className="text-[9px] text-dark-muted uppercase mb-0.5">Pagamento</p>
                      <div className="flex items-center justify-center gap-1">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className={`${PAGAMENTO_ICONS[v.formaPagamento]} text-xs ${PAGAMENTO_COLORS[v.formaPagamento]}`}></i>
                        </div>
                        <p className="text-xs font-bold text-dark-text capitalize">{v.formaPagamento}</p>
                      </div>
                    </div>
                    <div className="bg-dark-card rounded-lg p-2 text-center">
                      <p className="text-[9px] text-dark-muted uppercase mb-0.5">Líquido</p>
                      <p className="text-xs font-bold font-mono text-neon">{fmt(v.valorLiquido)}</p>
                    </div>
                  </div>
                  {v.taxaCartao > 0 && (
                    <div className="flex items-center justify-between mt-1.5 px-1">
                      <span className="text-[10px] text-dark-muted">Bruto: <span className="line-through">{fmt(v.valorTotal)}</span></span>
                      <span className="text-[10px] text-red-400">taxa -{v.taxaCartao}%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </AppLayout>
  );
}
